import React, { ReactNode, useCallback } from 'react';
import { Box, Button, Flex } from '@chakra-ui/react';
import { X } from '@phosphor-icons/react';
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';

export interface BaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  placement?: 'center' | 'top' | 'bottom';
  closeOnInteractOutside?: boolean;
  showCloseButton?: boolean;
  role?: 'dialog' | 'alertdialog';
  trigger?: ReactNode;
  footer?: ReactNode;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  titleIcon?: ReactNode;
  isLoading?: boolean;
  error?: string | null;
  motionPreset?: 'slide-in-bottom' | 'slide-in-top' | 'scale';
  unmountOnExit?: boolean;
}

export interface DialogAction {
  label: string;
  onClick: () => void | Promise<void>;
  variant?: 'primary' | 'secondary' | 'danger' | 'outline';
  isLoading?: boolean;
  disabled?: boolean;
  width?: string;
  'aria-label'?: string;
}

export interface BaseDialogWithActionsProps extends BaseDialogProps {
  actions?: DialogAction[];
  showCancel?: boolean;
  cancelLabel?: string;
  onCancel?: () => void;
}

const getButtonStyles = (variant: DialogAction['variant']) => {
  switch (variant) {
    case 'primary':
      return {
        bg: 'primary.900',
        color: 'neutrals.0',
        _hover: { bg: 'primary.800' }
      };
    case 'danger':
      return {
        bg: 'error.700',
        color: 'neutrals.0',
        _hover: { bg: 'error.500' }
      };
    case 'outline':
      return {
        bg: 'neutrals.0',
        borderColor: 'primary.900',
        color: 'primary.900',
        variant: 'outline' as const,
        _hover: { bg: 'neutrals.50' }
      };
    case 'secondary':
    default:
      return {
        bg: 'background.secondary',
        borderColor: 'primary.900',
        color: 'primary.900',
        _hover: { bg: 'background.primary' }
      };
  }
};

export function BaseDialog({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  placement = 'center',
  closeOnInteractOutside = true,
  showCloseButton = true,
  role = 'dialog',
  trigger,
  footer,
  onOpenChange,
  className,
  titleIcon,
  isLoading = false,
  error,
  motionPreset = 'slide-in-bottom',
  unmountOnExit = true
}: BaseDialogProps) {
  const handleDialogClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  const handleOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      if (!open && !isLoading) {
        onClose();
      }
      onOpenChange?.(open);
    },
    [isLoading, onClose, onOpenChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) {
        handleDialogClose();
      }
    },
    [isLoading, handleDialogClose]
  );

  return (
    <DialogRoot
      role={role}
      placement={placement}
      size={size}
      open={isOpen}
      onOpenChange={handleOpenChange}
      closeOnInteractOutside={closeOnInteractOutside}
      motionPreset={motionPreset}
      unmountOnExit={unmountOnExit}
    >
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        borderRadius="12px"
        onKeyDown={handleKeyDown}
        aria-modal="true"
        className={className}
      >
        <DialogHeader>
          <DialogTitle>
            {titleIcon ? (
              <Flex alignItems="center" gap="8px">
                {titleIcon}
                <Box fontSize="24px" fontWeight="bold">
                  {title}
                </Box>
              </Flex>
            ) : (
              <Box fontSize="24px" fontWeight="bold">
                {title}
              </Box>
            )}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {children}
          {error && (
            <Box color="error.500" fontSize="16px" mt="16px" role="alert" aria-live="polite">
              {error}
            </Box>
          )}
        </DialogBody>
        {footer && <DialogFooter>{footer}</DialogFooter>}
        {showCloseButton && (
          <DialogCloseTrigger
            m="12px"
            disabled={isLoading}
            aria-label="Close dialog"
            onClick={handleDialogClose}
          >
            <X size={32} weight="bold" />
          </DialogCloseTrigger>
        )}
      </DialogContent>
    </DialogRoot>
  );
}

export function BaseDialogWithActions({
  actions = [],
  showCancel = true,
  cancelLabel = 'Cancel',
  onCancel,
  ...props
}: BaseDialogWithActionsProps) {
  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      props.onClose();
    }
  }, [onCancel, props]);

  const footer = (
    <Flex gap="12px" justifyContent="flex-end">
      {showCancel && (
        <DialogActionTrigger asChild>
          <Button
            w="120px"
            fontSize="16px"
            borderRadius="8px"
            disabled={props.isLoading}
            onClick={handleCancel}
            aria-label={`Cancel ${props.title.toLowerCase()}`}
            {...getButtonStyles('secondary')}
          >
            {cancelLabel}
          </Button>
        </DialogActionTrigger>
      )}
      {actions.map((action, index) => (
        <Button
          key={index}
          w={action.width || '120px'}
          fontSize="16px"
          borderRadius="8px"
          onClick={action.onClick}
          loading={action.isLoading}
          disabled={action.disabled || props.isLoading}
          aria-label={action['aria-label'] || action.label}
          {...getButtonStyles(action.variant || 'primary')}
        >
          {action.label}
        </Button>
      ))}
    </Flex>
  );

  return <BaseDialog {...props} footer={footer} />;
}

// Specialized dialog types for common use cases
export interface ConfirmationDialogProps
  extends Omit<BaseDialogWithActionsProps, 'actions' | 'children'> {
  message: string;
  onConfirm: () => void | Promise<void>;
  confirmLabel?: string;
  confirmVariant?: DialogAction['variant'];
  isConfirmLoading?: boolean;
  isDangerous?: boolean;
}

export function ConfirmationDialog({
  message,
  onConfirm,
  confirmLabel = 'Confirm',
  confirmVariant = 'primary',
  isConfirmLoading = false,
  isDangerous = false,
  ...props
}: ConfirmationDialogProps) {
  const actions: DialogAction[] = [
    {
      label: confirmLabel,
      onClick: onConfirm,
      variant: isDangerous ? 'danger' : confirmVariant,
      isLoading: isConfirmLoading,
      'aria-label': `${confirmLabel} ${props.title.toLowerCase()}`
    }
  ];

  return (
    <BaseDialogWithActions
      {...props}
      role="alertdialog"
      actions={actions}
      closeOnInteractOutside={false}
    >
      <Box>
        <Box borderBottom="1px solid" borderColor="neutrals.100" mb="16px" />
        <Box fontSize="18px" py="16px" px="16px" mb="16px" color="neutrals.700">
          {message}
        </Box>
        <Box borderBottom="1px solid" borderColor="neutrals.100" />
      </Box>
    </BaseDialogWithActions>
  );
}

export interface FormDialogProps extends BaseDialogProps {
  onSubmit: (e: React.FormEvent) => void;
  submitLabel?: string;
  submitVariant?: DialogAction['variant'];
  isSubmitLoading?: boolean;
  isSubmitDisabled?: boolean;
  showCancel?: boolean;
  cancelLabel?: string;
  onCancel?: () => void;
}

export function FormDialog({
  onSubmit,
  submitLabel = 'Save',
  submitVariant = 'primary',
  isSubmitLoading = false,
  isSubmitDisabled = false,
  showCancel = true,
  cancelLabel = 'Cancel',
  onCancel,
  children,
  ...props
}: FormDialogProps) {
  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      props.onClose();
    }
  }, [onCancel, props]);

  const footer = (
    <Flex gap="12px" justifyContent="flex-end">
      {showCancel && (
        <DialogActionTrigger asChild>
          <Button
            w="120px"
            fontSize="16px"
            borderRadius="8px"
            disabled={isSubmitLoading || props.isLoading}
            onClick={handleCancel}
            aria-label={`Cancel ${props.title.toLowerCase()}`}
            {...getButtonStyles('secondary')}
          >
            {cancelLabel}
          </Button>
        </DialogActionTrigger>
      )}
      <Button
        type="submit"
        w="120px"
        fontSize="16px"
        borderRadius="8px"
        loading={isSubmitLoading}
        disabled={isSubmitDisabled || props.isLoading}
        aria-label={`${submitLabel} ${props.title.toLowerCase()}`}
        {...getButtonStyles(submitVariant)}
      >
        {submitLabel}
      </Button>
    </Flex>
  );

  return (
    <BaseDialog {...props} footer={footer}>
      <form onSubmit={onSubmit}>{children}</form>
    </BaseDialog>
  );
}

export default BaseDialog;
